import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { translations } from "./translations";
import txCatalog from "./tx.json";

// --- Module-level axios interceptor -----------------------------------------
// We install this ONCE on module load (before any React effect runs), and derive
// the language from window.location on each request. That way, the very first
// fetch after page load already carries the correct `?lang=` param.
const _readLangFromLocation = () => {
  try {
    const first = window.location.pathname.split("/").filter(Boolean)[0];
    if (first && ["sq", "en", "it", "de", "fr", "mk", "el"].includes(first) && first !== "sq") {
      return first;
    }
    return "sq";
  } catch (_) {
    return "sq";
  }
};

if (typeof window !== "undefined" && !window.__aaa_lang_interceptor__) {
  window.__aaa_lang_interceptor__ = true;
  axios.interceptors.request.use((config) => {
    const method = (config.method || "get").toLowerCase();
    const url = config.url || "";
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const isOurBackend = url.startsWith(backend + "/api") || url.startsWith("/api");
    const adminToken = isOurBackend ? window.localStorage?.getItem("admin_token") : null;
    if (adminToken && config.headers && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
    if (method === "get" && isOurBackend) {
      const params = config.params || {};
      if (params.lang === undefined) {
        config.params = { ...params, lang: _readLangFromLocation() };
      }
    }
    return config;
  });
}

const LanguageContext = createContext();

export const languages = [
  { code: "sq", label: "AL", name: "Shqip", flag: "al" },
  { code: "en", label: "EN", name: "English", flag: "gb" },
  { code: "it", label: "IT", name: "Italiano", flag: "it" },
  { code: "de", label: "DE", name: "Deutsch", flag: "de" },
  { code: "fr", label: "FR", name: "Français", flag: "fr" },
  { code: "mk", label: "MK", name: "Makedonski", flag: "mk" },
  { code: "el", label: "GR", name: "Ελληνικά", flag: "gr" },
];

export const SUPPORTED_LANGS = languages.map((l) => l.code);
export const DEFAULT_LANG = "sq";

// Read the language code from URL path: /en/..., /it/..., etc. (sq has no prefix)
export const detectLangFromPath = (pathname) => {
  const first = pathname.split("/").filter(Boolean)[0];
  if (first && SUPPORTED_LANGS.includes(first) && first !== DEFAULT_LANG) {
    return first;
  }
  return DEFAULT_LANG;
};

// Strip the language prefix from the path
export const stripLangFromPath = (pathname) => {
  const first = pathname.split("/").filter(Boolean)[0];
  if (first && SUPPORTED_LANGS.includes(first) && first !== DEFAULT_LANG) {
    return "/" + pathname.split("/").filter(Boolean).slice(1).join("/");
  }
  return pathname || "/";
};

// Build a localized path: ('/evente', 'en') -> '/en/evente'
export const buildLocalizedPath = (path, lang) => {
  if (!path || typeof path !== "string") return path;
  if (!path.startsWith("/")) return path; // external or relative
  const cleanPath = stripLangFromPath(path);
  if (lang === DEFAULT_LANG) return cleanPath || "/";
  const suffix = cleanPath === "/" ? "" : cleanPath;
  return `/${lang}${suffix}`;
};

const BOT_RE = /bot|crawl|spider|slurp|lighthouse|facebookexternalhit|whatsapp|telegram|preview|headless/i;
const NO_AUTO_RE = /^\/(paneli|admin|pagesa)/;

// Picks the language on the first visit: manual choice > link prefix > country (IP) > browser.
const useAutoLanguage = (location, navigate) => {
  useEffect(() => {
    const path = location.pathname;
    if (detectLangFromPath(path) !== DEFAULT_LANG || path.split("/")[1] === DEFAULT_LANG) return;
    if (NO_AUTO_RE.test(path) || BOT_RE.test(navigator.userAgent || "")) return;
    const go = (target) => {
      if (SUPPORTED_LANGS.includes(target) && target !== DEFAULT_LANG) {
        navigate(buildLocalizedPath(path, target) + location.search + location.hash, { replace: true });
      }
    };
    let manual = null;
    let cached = null;
    try {
      manual = localStorage.getItem("lang");
      cached = sessionStorage.getItem("geo_lang");
    } catch (_) { /* ignore */ }
    if (manual) return go(manual);
    if (cached) return go(cached);
    const browser = (navigator.languages || [navigator.language || ""]).join(",");
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/geo/lang`, { params: { browser, lang: DEFAULT_LANG }, timeout: 4000 })
      .then(({ data }) => {
        try { sessionStorage.setItem("geo_lang", data.lang); } catch (_) { /* ignore */ }
        go(data.lang);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLangFromPath(location.pathname);
  useAutoLanguage(location, navigate);

  const t = (key) =>
    translations[lang]?.[key] || translations[DEFAULT_LANG]?.[key] || key;

  // Text-keyed lookup: Albanian source string -> translated string
  const tx = (text) => (lang === DEFAULT_LANG || !text ? text : txCatalog[lang]?.[text] || text);

  const setLang = (newLang) => {
    if (!SUPPORTED_LANGS.includes(newLang)) return;
    const cleanPath = stripLangFromPath(location.pathname);
    const newPath = buildLocalizedPath(cleanPath, newLang) + location.search + location.hash;
    navigate(newPath);
    try {
      localStorage.setItem("lang", newLang);
    } catch (e) {
      /* ignore */
    }
  };

  const localizePath = (path) => buildLocalizedPath(path, lang);

  const value = useMemo(
    () => ({ lang, setLang, t, tx, localizePath }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, location.pathname]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
